import { useCallback } from "react";
import { config } from "../config";
import { useEventsApi } from "./useEventsApi";

const ADMIN_VEHICLE_SOFTWARE_SCANS = `${config.apiUrl}/api/admin/vehicle-software-event-scans`;

/**
 * Admin vehicle software CSV scan archive + catalog coverage gaps (same auth as other admin GETs).
 */
export function useVehicleSoftwareEventScanApi() {
  const { callApi } = useEventsApi();

  const listReports = useCallback(
    async (params: { offset: number; limit: number; softwareName?: string }) => {
      const url = new URL(`${ADMIN_VEHICLE_SOFTWARE_SCANS}/`);
      url.searchParams.set("offset", String(params.offset));
      url.searchParams.set("limit", String(params.limit));
      if (params.softwareName) {
        url.searchParams.set("software_name", params.softwareName);
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const listRows = useCallback(
    async (reportId: number, params: { offset: number; limit: number }) => {
      const url = new URL(`${ADMIN_VEHICLE_SOFTWARE_SCANS}/${reportId}/rows/`);
      url.searchParams.set("offset", String(params.offset));
      url.searchParams.set("limit", String(params.limit));
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const listCoverageGaps = useCallback(
    async (params: {
      offset: number;
      limit: number;
      useLegacyEventCode: boolean;
      scanBasis: "all_reports" | "latest_per_software";
      /** Omit comparison codes in [10000, 20000) from gap results */
      excludeResolvingEvents?: boolean;
    }) => {
      const url = new URL(`${ADMIN_VEHICLE_SOFTWARE_SCANS}/coverage/gaps/`);
      url.searchParams.set("offset", String(params.offset));
      url.searchParams.set("limit", String(params.limit));
      url.searchParams.set("use_legacy_event_code", params.useLegacyEventCode ? "true" : "false");
      url.searchParams.set("scan_basis", params.scanBasis);
      if (params.excludeResolvingEvents) {
        url.searchParams.set("exclude_resolving_events", "true");
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const listCoverageGapsByNumberRange = useCallback(
    async (params: {
      useLegacyEventCode: boolean;
      scanBasis: "all_reports" | "latest_per_software";
      excludeResolvingEvents?: boolean;
    }) => {
      const url = new URL(`${ADMIN_VEHICLE_SOFTWARE_SCANS}/coverage/gaps/by-number-range/`);
      url.searchParams.set("use_legacy_event_code", params.useLegacyEventCode ? "true" : "false");
      url.searchParams.set("scan_basis", params.scanBasis);
      if (params.excludeResolvingEvents) {
        url.searchParams.set("exclude_resolving_events", "true");
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  return { listReports, listRows, listCoverageGaps, listCoverageGapsByNumberRange };
}
